import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Ticket, Users, Globe, Zap, DollarSign, Check, X as XIcon,
  Crown, Building2, LineChart, Sparkles, CalendarClock, MessageSquare,
  BadgeCheck, Repeat, Layers, ChevronRight, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
};

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-indigo-400 mb-4">{children}</p>
);

/* ---------- Hero product mockup (pure JSX/CSS) ---------- */
const DashboardMockup = () => {
  const bars = [38, 52, 44, 68, 58, 82, 74, 96, 88, 71, 92, 100];
  return (
    <div className="relative" data-testid="hero-product-mockup">
      <div className="absolute -inset-x-16 -top-10 bottom-0 bg-indigo-600/20 blur-[100px] rounded-full" />
      <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl overflow-hidden shadow-2xl">
        {/* window chrome */}
        <div className="flex items-center gap-2 px-5 h-11 border-b border-white/10 bg-white/[0.02]">
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <p className="ml-4 text-[11px] tracking-wide text-zinc-500 font-medium">Creator Hub · Overview</p>
          <span className="ml-auto flex items-center gap-1.5 text-[10px] text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/[0.06]">
          {/* left: metrics + chart */}
          <div className="lg:col-span-2 bg-[#0b0c12] p-5 sm:p-7">
            <div className="grid grid-cols-3 gap-4 mb-7">
              {[
                { label: 'Recurring / mo', value: '$4,820', delta: '+18%' },
                { label: 'Members', value: '1,284', delta: '+62' },
                { label: 'Tickets sold', value: '3,940', delta: '+9%' },
              ].map((m) => (
                <div key={m.label} className="text-left">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">{m.label}</p>
                  <p className="text-lg sm:text-2xl font-semibold text-white mt-1">{m.value}</p>
                  <p className="text-[10px] text-emerald-400 font-medium">{m.delta}</p>
                </div>
              ))}
            </div>
            <div className="flex items-end gap-1.5 h-28">
              {bars.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{ delay: 0.4 + i * 0.05, duration: 0.6, ease: 'easeOut' }}
                  className="flex-1 rounded-t-[3px]"
                  style={{ background: i > 8 ? 'linear-gradient(180deg,#818cf8,#4f46e5)' : 'rgba(255,255,255,0.10)' }}
                />
              ))}
            </div>
            <div className="flex justify-between mt-3 text-[9px] text-zinc-600 tracking-wider uppercase">
              <span>Jan</span><span>Apr</span><span>Jul</span><span>Oct</span><span>Dec</span>
            </div>
          </div>

          {/* right: activity */}
          <div className="bg-[#0b0c12] p-5 sm:p-6">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-4">Today</p>
            <div className="space-y-3.5">
              {[
                { icon: Repeat, text: '12 new subscribers', sub: 'Inner Circle · $15/mo' },
                { icon: CalendarClock, text: 'Warehouse Sessions', sub: 'Sat · 78% sold' },
                { icon: Building2, text: 'Private hire enquiry', sub: '80 guests · Mezzanine' },
                { icon: MessageSquare, text: '34 posts in feed', sub: 'Tier-locked · VIP' },
              ].map((row) => (
                <div key={row.text} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center shrink-0">
                    <row.icon className="w-3.5 h-3.5 text-indigo-300" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-zinc-200 truncate">{row.text}</p>
                    <p className="text-[10px] text-zinc-500 truncate">{row.sub}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-xl border border-indigo-500/25 bg-indigo-500/10 p-3">
              <p className="text-[10px] uppercase tracking-wider text-indigo-300">Payout Friday</p>
              <p className="text-lg font-semibold text-white">$11,308.40</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Bento pillar card ---------- */
const PillarCard = ({ id, icon: Icon, title, desc, bullets, className = '', children }) => (
  <motion.div {...fadeUp} id={id} className={`group relative rounded-2xl border border-white/10 bg-white/[0.02] p-7 hover:border-indigo-500/40 hover:bg-white/[0.04] transition-colors ${className}`}>
    <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center mb-5">
      <Icon className="w-5 h-5 text-indigo-300" />
    </div>
    <h3 className="text-xl sm:text-2xl font-semibold text-white tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed max-w-md">{desc}</p>
    {bullets && (
      <ul className="mt-5 space-y-2">
        {bullets.map((b) => (
          <li key={b} className="flex items-center gap-2 text-[13px] text-zinc-300">
            <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {b}
          </li>
        ))}
      </ul>
    )}
    {children}
  </motion.div>
);

export default function Home() {
  const navigate = useNavigate();
  const [hubs, setHubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingHubs, setLoadingHubs] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/creators/directory?limit=6').catch(() => ({ data: { creators: [] } })),
      api.get('/monetization/plans').catch(() => ({ data: { plans: [] } })),
      api.get('/platform/stats').catch(() => ({ data: null })),
    ]).then(([hubRes, planRes, statRes]) => {
      setHubs(hubRes.data.creators || []);
      setPlans(planRes.data.plans || []);
      setStats(statRes.data);
      setLoadingHubs(false);
    });
  }, []);

  return (
    <ConsumerLayout darkTheme>
      <div className="bg-[#08090d]" data-testid="home-saas-page">

        {/* ================= HERO ================= */}
        <section className="relative overflow-hidden pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="absolute inset-0 opacity-[0.35]" style={{ backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.045) 1px, transparent 1px)', backgroundSize: '64px 64px', maskImage: 'radial-gradient(ellipse 70% 55% at 50% 0%, #000 40%, transparent 100%)', WebkitMaskImage: 'radial-gradient(ellipse 70% 55% at 50% 0%, #000 40%, transparent 100%)' }} />
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-3xl mx-auto mb-14">
              <Badge className="mb-7 bg-white/[0.04] text-zinc-300 border-white/10 hover:bg-white/[0.06] text-xs px-3.5 py-1.5 rounded-full font-medium">
                <Sparkles className="w-3 h-3 mr-1.5 text-indigo-400" /> The operating system for event communities
              </Badge>
              <p className="text-lg sm:text-xl font-semibold tracking-tight text-indigo-300 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="hero-eyebrow">
                Event Creators!
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter leading-[1.05] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Turn your community
                <br className="hidden sm:block" />{' '}
                into a <span className="italic bg-gradient-to-r from-indigo-300 via-indigo-400 to-violet-400 bg-clip-text text-transparent">business</span>.
              </h1>
              <p className="text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl mx-auto mb-9">
                EVENT★GOERS gives organisers, venues and creators one place to run events, sell tickets,
                host their members and earn recurring revenue — without renting their own crowd back from a ticketing giant.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <Button size="lg" data-testid="hero-start-free-btn" onClick={() => navigate('/my-hubs')}
                  className="h-12 px-8 rounded-full bg-white text-[#08090d] hover:bg-zinc-200 font-semibold text-base active:scale-95 transition-transform">
                  Start free <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" data-testid="hero-explore-communities-btn" onClick={() => navigate('/creators')}
                  className="h-12 px-8 rounded-full border-white/15 bg-white/[0.02] text-zinc-200 hover:bg-white/[0.07] hover:text-white text-base">
                  Explore communities
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-5">No credit card. Your hub is live in under 5 minutes.</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
              <DashboardMockup />
            </motion.div>
          </div>
        </section>

        {/* ================= WHO IT'S FOR STRIP ================= */}
        <section className="border-y border-white/[0.07] py-10">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-[10px] text-center font-semibold uppercase tracking-[0.24em] text-zinc-500 mb-6">Built for the people who run the night</p>
            <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
              {['Nightclubs', 'Promoters', 'Supper Clubs', 'Breweries', 'DJ Collectives', 'Festivals', 'Members Clubs', 'Run Clubs'].map((c) => (
                <span key={c} className="text-sm font-medium text-zinc-500 hover:text-zinc-300 transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>{c}</span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= PRODUCT PILLARS (BENTO) ================= */}
        <section className="py-24 lg:py-32" id="product">
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-2xl mb-14">
              <SectionLabel>The platform</SectionLabel>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Everything you were paying four tools for.
              </h2>
              <p className="text-base md:text-lg text-zinc-400 leading-relaxed">
                Your hub, your ticketing, your community and your revenue — one dashboard, one member list, one payout.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <PillarCard
                id="hubs" icon={Globe} className="md:col-span-2"
                title="Creator Hubs"
                desc="A branded home for your brand — events, media, links, and a members feed on your own handle. Replace the link-in-bio, the mailing list and the microsite."
                bullets={['Custom handle at /creators/yourname', 'Accent colours, cover art and featured media', 'Your followers, your emails — exportable anytime']}
              >
                <div className="mt-7 rounded-xl border border-white/10 bg-[#0b0c12] p-4 flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">Warehouse Sessions</p>
                    <p className="text-[11px] text-zinc-500">eventgoers.com/creators/warehouse</p>
                  </div>
                  <span className="ml-auto text-[10px] px-2.5 py-1 rounded-full bg-white/[0.06] text-zinc-300 shrink-0">Follow</span>
                </div>
              </PillarCard>

              <PillarCard
                id="ticketing" icon={Ticket}
                title="Modern ticketing"
                desc="Multi-tier tickets, add-ons, guest checkout and instant revenue splits with your promoters."
                bullets={['Tiers, add-ons & capacity caps', 'Automatic split payouts']}
              >
                <div className="mt-7 rounded-xl border border-white/10 bg-[#0b0c12] overflow-hidden">
                  {[{ n: 'General', p: '$25', s: 'Sold out' }, { n: 'Late release', p: '$35', s: '42 left' }, { n: 'Table of 6', p: '$300', s: '3 left' }].map((t, i) => (
                    <div key={t.n} className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? 'border-t border-white/[0.07]' : ''}`}>
                      <Ticket className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                      <p className="text-[12px] text-zinc-300 truncate">{t.n}</p>
                      <span className="ml-auto text-[12px] font-semibold text-white">{t.p}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${t.s === 'Sold out' ? 'bg-white/[0.06] text-zinc-500' : 'bg-emerald-500/15 text-emerald-400'}`}>{t.s}</span>
                    </div>
                  ))}
                </div>
              </PillarCard>

              <PillarCard
                id="community" icon={Users}
                title="Community"
                desc="Tier-locked feeds, member badges and leaderboards that keep your crowd talking between events."
                bullets={['Free & paid tiers', 'Members-only posts']}
              >
                <div className="mt-7 rounded-xl border border-white/10 bg-[#0b0c12] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-fuchsia-500 to-indigo-500 shrink-0" />
                    <p className="text-[11px] text-zinc-300">Guest list drop, 8pm</p>
                    <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300">VIP</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/[0.06] mb-1.5" />
                  <div className="h-1.5 rounded-full bg-white/[0.06] w-2/3 mb-3" />
                  <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                    <span>♥ 214</span><span>34 replies</span>
                  </div>
                </div>
              </PillarCard>

              <PillarCard
                id="revenue" icon={DollarSign} className="md:col-span-2"
                title="Recurring revenue"
                desc="Fan subscriptions, checkout add-ons and an ambassador programme that turns your best regulars into your sales team."
                bullets={['Monthly memberships with perks', 'Referral tracking & ambassador payouts', 'One payout, full fee transparency']}
              >
                <div className="mt-7 grid grid-cols-3 gap-3">
                  {[{ n: 'Inner Circle', p: '$15' }, { n: 'Front Row', p: '$29' }, { n: 'Backstage', p: '$79' }].map((t, i) => (
                    <div key={t.n} className={`rounded-xl border p-3.5 ${i === 1 ? 'border-indigo-500/40 bg-indigo-500/10' : 'border-white/10 bg-[#0b0c12]'}`}>
                      <p className="text-[11px] text-zinc-400">{t.n}</p>
                      <p className="text-base font-semibold text-white">{t.p}<span className="text-[10px] text-zinc-500 font-normal">/mo</span></p>
                    </div>
                  ))}
                </div>
              </PillarCard>
            </div>
          </div>
        </section>

        {/* ================= SOLUTIONS SPLIT ================= */}
        <section className="py-24 lg:py-32 border-t border-white/[0.07]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-2xl mb-14">
              <SectionLabel>Solutions</SectionLabel>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Two sides of the same night.
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                {
                  id: 'for-organisers',
                  eyebrow: 'For organisers, DJs & promoters',
                  title: 'Sell out shows and keep the audience',
                  desc: 'Launch an event page in minutes, sell tiered tickets, split revenue with collaborators, then keep the crowd engaged in your own community feed until the next one.',
                  points: ['Event pages & tiered ticketing', 'Revenue splits with promoters', 'Fan subscriptions & ambassador links', 'Exportable audience data'],
                  cta: 'Start free', to: '/my-hubs',
                  img: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxjb25jZXJ0JTIwY3Jvd2QlMjBzaWxob3VldHRlfGVufDB8fHx8MTc4NzQ0ODc4OXww&ixlib=rb-4.1.0&q=85',
                  testid: 'solutions-organisers',
                },
                {
                  id: 'for-venues',
                  eyebrow: 'For venues',
                  title: 'Fill the calendar, not the inbox',
                  desc: 'Private hire packages, enquiry routing, reservations and in-house programming — managed from one console with the promoters who use your rooms.',
                  points: ['Private hire packages & enquiries', 'Reservations & table bookings', 'Multi-room programming calendar', 'Promoter accounts & house splits'],
                  cta: 'Talk to our team', to: '/partner/register',
                  img: 'https://images.pexels.com/photos/17001732/pexels-photo-17001732.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
                  testid: 'solutions-venues',
                },
              ].map((s) => (
                <motion.div key={s.id} {...fadeUp} id={s.id} data-testid={s.testid}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-white/20 transition-colors">
                  <div className="h-52 sm:h-60 relative overflow-hidden">
                    <img src={s.img} alt={s.eyebrow} className="w-full h-full object-cover opacity-60 hover:opacity-75 hover:scale-[1.03] transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#08090d] via-[#08090d]/40 to-transparent" />
                  </div>
                  <div className="p-7 lg:p-9 -mt-10 relative">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-400 mb-3">{s.eyebrow}</p>
                    <h3 className="text-2xl md:text-3xl font-semibold text-white tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.title}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed mb-6">{s.desc}</p>
                    <ul className="space-y-2.5 mb-8">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-center gap-2.5 text-[13px] text-zinc-300">
                          <BadgeCheck className="w-4 h-4 text-indigo-400 shrink-0" /> {p}
                        </li>
                      ))}
                    </ul>
                    <Button variant="outline" data-testid={`${s.testid}-cta`} onClick={() => navigate(s.to)}
                      className="rounded-full border-white/15 bg-white/[0.03] text-zinc-100 hover:bg-white/[0.09] hover:text-white">
                      {s.cta} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= HOW IT WORKS ================= */}
        <section className="py-24 lg:py-28 border-t border-white/[0.07]" id="how-it-works">
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-2xl mb-14">
              <SectionLabel>How it works</SectionLabel>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Live tonight, compounding by next season.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.07] rounded-2xl overflow-hidden border border-white/[0.07]">
              {[
                { n: '01', icon: Globe, t: 'Create your hub', d: 'Claim your handle, add your brand, connect payouts. Five minutes, no developer.' },
                { n: '02', icon: Layers, t: 'Launch your events', d: 'Build the page, set your tiers and add-ons, publish and share one link everywhere.' },
                { n: '03', icon: LineChart, t: 'Grow the community', d: 'Convert ticket buyers into paying members with feeds, perks and recurring tiers.' },
              ].map((s, i) => (
                <motion.div key={s.n} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="bg-[#0b0c12] p-8 lg:p-10">
                  <p className="text-[11px] font-mono text-indigo-400 mb-6">{s.n}</p>
                  <s.icon className="w-6 h-6 text-zinc-300 mb-5" />
                  <h3 className="text-xl font-semibold text-white tracking-tight mb-2.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.t}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= OWN YOUR AUDIENCE (COMPARISON) ================= */}
        <section className="py-24 lg:py-32 border-t border-white/[0.07]">
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <motion.div {...fadeUp}>
              <SectionLabel>Own your audience</SectionLabel>
              <h2 className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Stop renting your own crowd.
              </h2>
              <p className="text-base text-zinc-400 leading-relaxed mb-8">
                Legacy ticketing keeps the buyer and sells them someone else's show. Here, every member,
                every email and every repeat purchase belongs to you.
              </p>
              <Button data-testid="comparison-start-free-btn" onClick={() => navigate('/my-hubs')}
                className="h-12 px-7 rounded-full bg-white text-[#08090d] hover:bg-zinc-200 font-semibold active:scale-95 transition-transform">
                Claim your handle <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>

            <motion.div {...fadeUp} className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-5">Legacy ticketing</p>
                {['They own the buyer', 'No community layer', 'One-off transactions', 'Fees you can\'t explain'].map((t) => (
                  <div key={t} className="flex items-start gap-2 mb-3.5 last:mb-0">
                    <XIcon className="w-4 h-4 text-rose-400/70 shrink-0 mt-0.5" />
                    <p className="text-[13px] text-zinc-500">{t}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-indigo-500/30 bg-indigo-500/[0.07] p-6">
                <p className="text-[11px] uppercase tracking-wider text-indigo-300 mb-5">EVENT★GOERS</p>
                {['You own the member list', 'Feeds, tiers & badges', 'Recurring subscriptions', 'Transparent, lower fees'].map((t) => (
                  <div key={t} className="flex items-start gap-2 mb-3.5 last:mb-0">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <p className="text-[13px] text-zinc-200">{t}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ================= FIND YOUR COMMUNITY ================= */}
        <section className="py-24 lg:py-32 border-t border-white/[0.07]" id="communities">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
              <div className="max-w-xl">
                <SectionLabel>For eventgoers</SectionLabel>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Find your community.
                </h2>
                <p className="text-base text-zinc-400 leading-relaxed">
                  Follow the organisers, venues and DJs you actually go out for. Get their drops first, join the feed, become a member.
                </p>
              </div>
              <Link to="/creators" data-testid="communities-view-all-link">
                <Button variant="outline" className="rounded-full border-white/15 bg-white/[0.03] text-zinc-200 hover:bg-white/[0.09] hover:text-white shrink-0">
                  Browse all communities <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            {loadingHubs ? (
              <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-zinc-600 animate-spin" /></div>
            ) : hubs.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-communities-grid">
                {hubs.slice(0, 6).map((hub) => (
                  <Link key={hub.id} to={`/creators/${hub.handle}`} data-testid={`community-card-${hub.handle}`}>
                    <motion.div whileHover={{ y: -4 }} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-indigo-500/40 transition-colors h-full">
                      <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 rounded-full overflow-hidden border border-white/10 shrink-0" style={{ backgroundColor: `${hub.accent_color || '#4f46e5'}22` }}>
                          {hub.avatar
                            ? <img src={hub.avatar} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center text-lg font-semibold" style={{ color: hub.accent_color || '#818cf8' }}>{hub.stage_name?.charAt(0)}</div>}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-white truncate">{hub.stage_name}</p>
                          <p className="text-[11px] text-zinc-500 capitalize truncate">{hub.hub_type} · {hub.city || 'Denver'}</p>
                        </div>
                      </div>
                      {hub.tagline && <p className="text-sm text-zinc-400 line-clamp-2 mb-5">{hub.tagline}</p>}
                      <div className="flex items-center justify-between text-[11px] text-zinc-500">
                        <span>{hub.followers_count || 0} members</span>
                        <span className="text-indigo-400 font-medium">View hub →</span>
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-white/12 bg-white/[0.015] px-8 py-16 text-center" data-testid="communities-empty-state">
                <Crown className="w-9 h-9 text-indigo-400/70 mx-auto mb-5" />
                <h3 className="text-xl font-semibold text-white mb-2.5" style={{ fontFamily: "'Outfit', sans-serif" }}>The first communities are being built right now.</h3>
                <p className="text-sm text-zinc-400 max-w-md mx-auto mb-7">
                  Claim your handle today and be one of the founding hubs eventgoers discover here.
                </p>
                <Button data-testid="communities-empty-cta" onClick={() => navigate('/my-hubs')}
                  className="h-11 px-7 rounded-full bg-white text-[#08090d] hover:bg-zinc-200 font-semibold">
                  Create your hub <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* ================= PRICING TEASER ================= */}
        {plans.length > 0 && (
          <section className="py-24 lg:py-32 border-t border-white/[0.07]">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center max-w-2xl mx-auto mb-14">
                <SectionLabel>Pricing</SectionLabel>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Start free. Pay when it pays you.
                </h2>
                <p className="text-base text-zinc-400">Every plan includes ticketing, a hub and a community feed.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="pricing-teaser-grid">
                {plans.map((plan) => (
                  <motion.div key={plan.id} {...fadeUp}
                    className={`rounded-2xl border p-7 flex flex-col ${plan.popular ? 'border-indigo-500/45 bg-indigo-500/[0.07]' : 'border-white/10 bg-white/[0.02]'}`}
                    data-testid={`pricing-teaser-${plan.id}`}>
                    {plan.popular && <Badge className="self-start mb-4 bg-indigo-500 text-white border-0 text-[10px] rounded-full px-2.5">Most popular</Badge>}
                    <p className="text-sm font-semibold text-zinc-300" style={{ fontFamily: "'Outfit', sans-serif" }}>{plan.name}</p>
                    <div className="flex items-baseline gap-1 mt-3 mb-1">
                      <span className="text-4xl font-semibold text-white tracking-tight">{plan.price_monthly === 0 ? 'Free' : `$${plan.price_monthly}`}</span>
                      {plan.price_monthly > 0 && <span className="text-xs text-zinc-500">/mo</span>}
                    </div>
                    <p className="text-xs text-zinc-500 mb-6">{plan.description}</p>
                    <ul className="space-y-2.5 mb-8">
                      {(plan.features || []).slice(0, 4).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[13px] text-zinc-300">
                          <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" /> {f}
                        </li>
                      ))}
                    </ul>
                    <Button onClick={() => navigate('/pricing')} data-testid={`pricing-teaser-cta-${plan.id}`}
                      className={`mt-auto w-full rounded-full font-semibold ${plan.popular ? 'bg-white text-[#08090d] hover:bg-zinc-200' : 'bg-white/[0.06] text-zinc-100 hover:bg-white/[0.12]'}`}>
                      {plan.price_monthly === 0 ? 'Start free' : 'See full plan'}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ================= METRICS ================= */}
        {stats && (
          <section className="py-16 border-t border-white/[0.07]">
            <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-10 text-center" data-testid="platform-stats">
              {[
                { label: 'Venues on platform', value: stats.partner_venues || 0 },
                { label: 'Communities', value: hubs.length },
                { label: 'Events managed', value: stats.total_events || 0 },
                { label: 'Cities live', value: 1 },
              ].filter((s) => s.value > 0).map((s) => (
                <div key={s.label}>
                  <p className="text-3xl md:text-4xl font-semibold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {s.value > 1000 ? `${(s.value / 1000).toFixed(1)}K` : s.value}
                  </p>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mt-2">{s.label}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ================= FINAL CTA ================= */}
        <section className="relative py-24 lg:py-32 border-t border-white/[0.07] overflow-hidden">
          <div className="absolute left-1/2 -translate-x-1/2 top-0 w-[600px] h-[300px] bg-indigo-600/20 blur-[120px] rounded-full" />
          <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
            <Zap className="w-9 h-9 text-indigo-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Your community is already there.<br />Give it a home.
            </h2>
            <p className="text-base text-zinc-400 mb-9 max-w-xl mx-auto">
              Set up your hub free, run your next event on it, and see what owning the relationship is actually worth.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" data-testid="final-start-free-btn" onClick={() => navigate('/my-hubs')}
                className="h-12 px-8 rounded-full bg-white text-[#08090d] hover:bg-zinc-200 font-semibold text-base active:scale-95 transition-transform">
                Start free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" data-testid="final-pricing-btn" onClick={() => navigate('/pricing')}
                className="h-12 px-8 rounded-full border-white/15 bg-white/[0.02] text-zinc-200 hover:bg-white/[0.07] hover:text-white text-base">
                See pricing
              </Button>
            </div>
          </div>
        </section>
      </div>
    </ConsumerLayout>
  );
}
